import { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import { formatCurrency, getMonthsShort } from '../utils';
import {
  ComposedChart, Area, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid, Cell,
} from 'recharts';
import { IconChevronRight, IconEdit, IconCheck, IconX, IconUsers } from '../components/Icons';
import Modal from '../components/Modal';
import { useToast } from '../components/Toast';
import { Skeleton, SkeletonCard } from '../components/Skeleton';
import HelpHint from '../components/HelpHint';

// ─── helpers ──────────────────────────────────────────────────────────────────

function fmtK(v) {
  if (v == null) return '0';
  if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k`;
  return Math.round(v).toString();
}

// ─── KPI z opcjonalnym kliknięciem ───────────────────────────────────────────

function KPI({ label, value, sub, color, onClick, hint }) {
  const clickable = !!onClick;
  const inner = (
    <>
      <p style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)', marginBottom: '4px' }}>{label}</p>
      <p style={{ fontSize: '20px', fontWeight: 800, fontFamily: 'monospace', color: color || 'var(--ink)' }}>{value}</p>
      <div className="flex items-center justify-between gap-2 mt-0.5">
        {sub && <p style={{ fontSize: '10px', color: 'var(--ink3)' }}>{sub}</p>}
        {clickable && (
          <span style={{ color: 'var(--ink3)', flexShrink: 0, opacity: 0.6 }} title={hint}>
            <IconChevronRight className="w-3 h-3" />
          </span>
        )}
      </div>
    </>
  );
  if (!clickable) return <div className="ds-card p-4">{inner}</div>;
  return (
    <button onClick={onClick}
      className="ds-card p-4 transition-all"
      style={{ textAlign: 'left', cursor: 'pointer', width: '100%' }}
      onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(184,84,40,0.4)'}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
      {inner}
    </button>
  );
}

// ─── Top klienci — z getFakturyAll ───────────────────────────────────────────

function TopKlienciCard({ onNavigate }) {
  const { t } = useTranslation();
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.getFakturyAll().then(all => {
      const today = new Date();
      const cutoff = new Date(today.getFullYear(), today.getMonth() - 11, 1);
      const cutoffIso = cutoff.toISOString().slice(0, 7);

      const paid = all.filter(f => {
        if (f.status !== 'zaplacona') return false;
        const ref = f.data_wplaty || f.data_wystawienia || '';
        return ref.slice(0, 7) >= cutoffIso;
      });

      const byClient = {};
      paid.forEach(f => {
        const key = f.klient_id != null ? `c${f.klient_id}` : `_${f.klient_nazwa || 'unknown'}`;
        if (!byClient[key]) byClient[key] = { id: f.klient_id, nazwa: f.klient_nazwa || '—', total: 0, count: 0 };
        byClient[key].total += Number(f.kwota) || 0;
        byClient[key].count += 1;
      });

      const list = Object.values(byClient).sort((a, b) => b.total - a.total).slice(0, 10);
      setItems(list);
    }).catch(() => setItems([]));
  }, []);

  if (!items) {
    return (
      <div className="ds-card p-5">
        <Skeleton w="40%" h="13px" />
        <div style={{ marginTop: '12px' }}><Skeleton w="100%" h="11px" /></div>
      </div>
    );
  }

  if (items.length === 0) return null;

  const grandTotal = items.reduce((s, c) => s + c.total, 0);

  return (
    <div className="ds-card p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 style={{ fontSize: '11px', fontWeight: 700, color: 'var(--ink)' }} className="flex items-center gap-2">
          <IconUsers className="w-4 h-4" style={{ color: 'var(--ok2)' }} />
          {t('dashboardV2.topClientsTitle', { n: items.length })}
        </h2>
        <span style={{ fontSize: '10px', color: 'var(--ink3)', fontFamily: 'monospace', fontWeight: 700 }}>
          {formatCurrency(grandTotal)}
        </span>
      </div>
      <div className="flex flex-col">
        {items.map((c, i) => {
          const pct = grandTotal > 0 ? (c.total / grandTotal) * 100 : 0;
          const clickable = c.id != null;
          const Wrap = clickable ? 'button' : 'div';
          return (
            <Wrap
              key={c.id ?? `_${i}`}
              onClick={clickable ? () => onNavigate('klient', c.id) : undefined}
              className={clickable ? 'text-left w-full' : ''}
              style={{
                padding: '8px 0',
                borderTop: i === 0 ? 'none' : '1px solid var(--border)',
                background: 'none', border: 'none',
                cursor: clickable ? 'pointer' : 'default',
              }}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span style={{ fontSize: '11px', color: 'var(--ink3)', fontFamily: 'monospace', minWidth: '20px', fontWeight: 700 }}>
                    #{i + 1}
                  </span>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--ink)' }} className="truncate">{c.nazwa}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span style={{ fontSize: '10px', color: 'var(--ink3)' }}>
                    {pct.toFixed(0)}%
                  </span>
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--ok2)', fontSize: '12px', minWidth: '85px', textAlign: 'right' }}>
                    {formatCurrency(c.total)}
                  </span>
                </div>
              </div>
              {/* Pasek udziału */}
              <div style={{ marginTop: '4px', height: '3px', background: 'var(--bg4)', borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, background: 'var(--ok2)', borderRadius: '2px' }} />
              </div>
            </Wrap>
          );
        })}
      </div>
    </div>
  );
}

// ─── Modal: KPI breakdown ─────────────────────────────────────────────────────

function FinanseKPIModal({ type, wykresData, roczne, onClose }) {
  const { t } = useTranslation();
  if (!type) return null;
  const TITLES = {
    sprzedaz: t('dashboardV2.modalSubscriptionAnnual'),
    zysk: t('dashboardV2.modalProfitAnnual'),
    marza: t('dashboardV2.modalMargin'),
    sredni: t('dashboardV2.modalAvgMonthly'),
  };

  return (
    <Modal title={TITLES[type] || ''} onClose={onClose}>
      <div className="flex flex-col gap-3">
        <div style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
          <div className="flex justify-between gap-3">
            <span style={{ fontSize: '12px', color: 'var(--ink2)', fontWeight: 600 }}>{t('finances.revenueHosting')}</span>
            <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--ok2)', fontSize: '13px' }}>
              {formatCurrency(roczne.hosting)}
            </span>
          </div>
        </div>
        <div style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
          <div className="flex justify-between gap-3">
            <span style={{ fontSize: '12px', color: 'var(--ink2)', fontWeight: 600 }}>{t('finances.packages_col')}</span>
            <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--acc2)', fontSize: '13px' }}>
              {formatCurrency(roczne.pakiety)}
            </span>
          </div>
        </div>
        <div style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
          <div className="flex justify-between gap-3">
            <span style={{ fontSize: '12px', color: 'var(--ink2)', fontWeight: 600 }}>{t('dashboardV2.totalSumLine')}</span>
            <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--ink)', fontSize: '13px' }}>
              {formatCurrency(roczne.przychod)}
            </span>
          </div>
        </div>
        <div style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
          <div className="flex justify-between gap-3">
            <span style={{ fontSize: '12px', color: 'var(--ink2)', fontWeight: 600 }}>{t('dashboardV2.subtractCostsLine')}</span>
            <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--err2)', fontSize: '13px' }}>
              {formatCurrency(roczne.koszt)}
            </span>
          </div>
        </div>
        <div className="flex justify-between gap-3" style={{ padding: '12px 0 4px', borderTop: '2px solid var(--border)', marginTop: '4px' }}>
          <p style={{ fontSize: '11px', color: 'var(--ink)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('dashboardV2.equalsProfitLine')}</p>
          <span style={{ fontFamily: 'monospace', fontWeight: 800, color: roczne.zysk >= 0 ? 'var(--ok2)' : 'var(--err2)', fontSize: '18px' }}>
            {formatCurrency(roczne.zysk)}
          </span>
        </div>

        {roczne.przychod > 0 && (
          <div className="flex justify-between gap-3" style={{ padding: '8px 0' }}>
            <span style={{ fontSize: '11px', color: 'var(--ink3)' }}>{t('dashboardV2.margin')}</span>
            <span style={{ fontFamily: 'monospace', fontWeight: 700, color: roczne.zysk >= 0 ? 'var(--ok2)' : 'var(--err2)', fontSize: '12px' }}>
              {(roczne.zysk / roczne.przychod * 100).toFixed(1)}%
            </span>
          </div>
        )}

        {/* Najlepszy / najgorszy miesiąc */}
        {(type === 'zysk' || type === 'sredni') && wykresData.length > 0 && (() => {
          const sorted = [...wykresData].sort((a, b) => b._zysk - a._zysk);
          const best = sorted[0];
          const worst = sorted[sorted.length - 1];
          if (!best || best._zysk === 0) return null;
          return (
            <div style={{ marginTop: '12px' }}>
              <p style={{ fontSize: '9px', fontWeight: 700, color: 'var(--ink3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
                {t('dashboardV2.extremeMonths')}
              </p>
              <div className="flex justify-between" style={{ padding: '6px 0', fontSize: '11px' }}>
                <span style={{ color: 'var(--ink3)' }}>{t('dashboardV2.bestMonth', { name: '' })}<span style={{ color: 'var(--ink)', fontWeight: 600 }}>{best.name}</span></span>
                <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--ok2)' }}>{formatCurrency(best._zysk)}</span>
              </div>
              {worst && worst._zysk < best._zysk && (
                <div className="flex justify-between" style={{ padding: '6px 0', fontSize: '11px' }}>
                  <span style={{ color: 'var(--ink3)' }}>{t('dashboardV2.worstMonth', { name: '' })}<span style={{ color: 'var(--ink)', fontWeight: 600 }}>{worst.name}</span></span>
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, color: worst._zysk < 0 ? 'var(--err2)' : 'var(--ink2)' }}>{formatCurrency(worst._zysk)}</span>
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </Modal>
  );
}

// ─── Główny FinanseV2 ─────────────────────────────────────────────────────────

export default function FinanseV2({ onNavigate }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [koszty, setKoszty] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editKoszt, setEditKoszt] = useState({});
  const [savingKoszt, setSavingKoszt] = useState(false);
  const [showZeroMonths, setShowZeroMonths] = useState(false);
  const [kpiModal, setKpiModal] = useState(null);

  const MONTHS = getMonthsShort();

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.getDashboard(), api.getKoszty()])
      .then(([d, k]) => { setData(d); setKoszty(k); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const wykresData = useMemo(() => {
    if (!data?.wykres) return [];
    return data.wykres.map(w => ({
      name: `${MONTHS[w.miesiac - 1].slice(0, 3)} ${String(w.rok).slice(2)}`,
      przychod: parseFloat((w.przychod || 0).toFixed(2)),
      pakiety: parseFloat((w.przychod_pakiety || 0).toFixed(2)),
      hosting: parseFloat((w.przychod_hosting || 0).toFixed(2)),
      koszt: parseFloat((w.koszt || 0).toFixed(2)),
      _zysk: parseFloat((w.zysk || 0).toFixed(2)),
      miesiac: w.miesiac,
      rok: w.rok,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const roczne = useMemo(() => {
    return wykresData.reduce(
      (acc, w) => ({
        hosting: acc.hosting + w.hosting,
        pakiety: acc.pakiety + w.pakiety,
        przychod: acc.przychod + w.przychod,
        koszt: acc.koszt + w.koszt,
        zysk: acc.zysk + w._zysk,
      }),
      { hosting: 0, pakiety: 0, przychod: 0, koszt: 0, zysk: 0 }
    );
  }, [wykresData]);

  const stats = useMemo(() => {
    const aktywne = wykresData.filter(w => w.przychod > 0 || w.koszt > 0);
    const monthsActive = aktywne.length;
    const sredniZysk = monthsActive > 0 ? roczne.zysk / monthsActive : 0;
    const marza = roczne.przychod > 0 ? (roczne.zysk / roczne.przychod) * 100 : 0;
    return { monthsActive, sredniZysk, marza };
  }, [wykresData, roczne]);

  const handleKosztChange = (rok, miesiac, val) =>
    setEditKoszt(prev => ({ ...prev, [`${rok}-${miesiac}`]: val }));

  const handleSaveKoszt = async (rok, miesiac) => {
    const key = `${rok}-${miesiac}`;
    setSavingKoszt(true);
    try {
      await api.saveKoszt({ rok, miesiac, kwota: Number(editKoszt[key]) || 0 });
      setEditKoszt(prev => { const n = { ...prev }; delete n[key]; return n; });
      toast.success(t('toast.saved'));
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSavingKoszt(false);
    }
  };

  if (loading) return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} lines={1} />)}
      </div>
      <SkeletonCard lines={4} />
      <SkeletonCard lines={3} />
    </div>
  );

  // Tabela: rozdziel "aktywne miesiące" od "puste" dla collapse
  const reversed = [...wykresData].reverse();
  const aktywneMies = reversed.filter(w => (w.hosting + w.pakiety + w.koszt) > 0);
  const puste = reversed.filter(w => (w.hosting + w.pakiety + w.koszt) === 0);
  const visibleMies = showZeroMonths ? reversed : aktywneMies;

  return (
    <div className="space-y-5">
      <div>
        <h1 style={{ fontSize: '20px', fontWeight: 700, color: 'var(--ink)', margin: '0 0 4px' }}>{t('finances.title')}</h1>
        <p style={{ fontSize: '12px', color: 'var(--ink3)', margin: 0 }}>{t('dashboard.last12months')}</p>
      </div>

      {/* Stat zone — 4 KPI */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KPI
          label={t('dashboardV2.kpiTotalSales')}
          value={formatCurrency(roczne.przychod)}
          color="var(--ok2)"
          sub={t('dashboardV2.monthCount', { n: stats.monthsActive, count: stats.monthsActive })}
          onClick={roczne.przychod > 0 ? () => setKpiModal('sprzedaz') : undefined}
          hint={t('dashboardV2.hintShowCostsBreakdown')}
        />
        <KPI
          label={t('dashboardV2.kpiTotalProfit')}
          value={formatCurrency(roczne.zysk)}
          color={roczne.zysk >= 0 ? 'var(--ok2)' : 'var(--err2)'}
          sub={`${t('dashboard.costs').toLowerCase()} ${formatCurrency(roczne.koszt)}`}
          onClick={() => setKpiModal('zysk')}
          hint={t('dashboardV2.hintShowExtremeMonths')}
        />
        <KPI
          label={t('dashboardV2.margin')}
          value={`${stats.marza.toFixed(0)}%`}
          color={stats.marza >= 50 ? 'var(--ok2)' : stats.marza >= 0 ? 'var(--acc2)' : 'var(--err2)'}
          sub={`${t('dashboard.profit').toLowerCase()} / ${t('dashboard.revenue').toLowerCase()}`}
          onClick={roczne.przychod > 0 ? () => setKpiModal('marza') : undefined}
        />
        <KPI
          label={t('dashboardV2.kpiAvgMonthlyProfit')}
          value={formatCurrency(stats.sredniZysk)}
          color={stats.sredniZysk >= 0 ? 'var(--ink)' : 'var(--err2)'}
          sub={stats.monthsActive > 0 ? t('dashboardV2.kpiAvgMonthlyProfitSub', { n: stats.monthsActive }) : null}
          onClick={stats.monthsActive > 0 ? () => setKpiModal('sredni') : undefined}
        />
      </div>

      <FinanseKPIModal type={kpiModal} wykresData={wykresData} roczne={roczne} onClose={() => setKpiModal(null)} />

      {/* Główny wykres: Przychód (gradient area) + Koszt (bars w tle) */}
      <div className="ds-card p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 style={{ fontSize: '11px', fontWeight: 700, color: 'var(--ink)' }}>{t('finances.revenuesAndCosts')}</h2>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={wykresData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="finRev" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="var(--ok2)" stopOpacity={0.45} />
                <stop offset="100%" stopColor="var(--ok2)" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="finProf" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="var(--acc)" stopOpacity={0.30} />
                <stop offset="100%" stopColor="var(--acc)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 5" stroke="var(--border)" vertical={false} />
            <XAxis
              dataKey="name"
              tick={{ fill: 'var(--ink3)', fontSize: 10 }}
              axisLine={false} tickLine={false} dy={8}
            />
            <YAxis
              tick={{ fill: 'var(--ink3)', fontSize: 10 }}
              axisLine={false} tickLine={false}
              width={48}
              tickFormatter={fmtK}
            />
            <Tooltip
              contentStyle={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border)',
                borderRadius: '10px',
                fontSize: '11px',
                boxShadow: 'var(--shadow-modal)',
                padding: '10px 12px',
              }}
              labelStyle={{ color: 'var(--ink)', fontWeight: 700, marginBottom: '6px', fontSize: '11px' }}
              itemStyle={{ color: 'var(--ink2)', padding: '2px 0' }}
              formatter={v => [formatCurrency(v)]}
              cursor={{ stroke: 'var(--border)', strokeWidth: 1, strokeDasharray: '3 5' }}
            />
            <Legend
              wrapperStyle={{ fontSize: 10, paddingTop: '14px' }}
              iconType="circle"
              iconSize={8}
              formatter={v => <span style={{ color: 'var(--ink3)', marginLeft: '4px', marginRight: '8px' }}>{v}</span>}
            />
            <Bar dataKey="koszt" name={t('finances.mpCost')} fill="var(--err2)" fillOpacity={0.18} radius={[3, 3, 0, 0]} maxBarSize={18} />
            <Area
              type="monotone"
              dataKey="przychod"
              name={t('dashboard.revenue')}
              stroke="var(--ok2)"
              strokeWidth={2.5}
              fill="url(#finRev)"
              dot={{ fill: 'var(--ok2)', r: 0 }}
              activeDot={{ r: 5, strokeWidth: 2, stroke: 'var(--bg-card)' }}
            />
            <Area
              type="monotone"
              dataKey="_zysk"
              name={t('dashboard.profit')}
              stroke="var(--acc)"
              strokeWidth={2}
              fill="url(#finProf)"
              dot={{ fill: 'var(--acc)', r: 0 }}
              activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--bg-card)' }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Wykres słupkowy zysku — kolor zależny od znaku */}
      <div className="ds-card p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 style={{ fontSize: '11px', fontWeight: 700, color: 'var(--ink)' }}>{t('finances.monthlyProfit')}</h2>
        </div>
        <ResponsiveContainer width="100%" height={180}>
          <ComposedChart data={wykresData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 5" stroke="var(--border)" vertical={false} />
            <XAxis dataKey="name" tick={{ fill: 'var(--ink3)', fontSize: 10 }} axisLine={false} tickLine={false} dy={8} />
            <YAxis tick={{ fill: 'var(--ink3)', fontSize: 10 }} axisLine={false} tickLine={false} width={48} tickFormatter={fmtK} />
            <Tooltip
              contentStyle={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border)',
                borderRadius: '10px',
                fontSize: '11px',
                boxShadow: 'var(--shadow-modal)',
                padding: '10px 12px',
              }}
              labelStyle={{ color: 'var(--ink)', fontWeight: 700, marginBottom: '6px' }}
              itemStyle={{ color: 'var(--ink2)' }}
              formatter={v => [formatCurrency(v)]}
              cursor={{ fill: 'rgba(255,255,255,0.03)' }}
            />
            <Bar dataKey="_zysk" name={t('dashboard.profit')} radius={[3, 3, 0, 0]} maxBarSize={28}>
              {wykresData.map((entry, i) => (
                <Cell key={i} fill={entry._zysk >= 0 ? 'var(--ok2)' : 'var(--err2)'} fillOpacity={0.85} />
              ))}
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Top klienci */}
      <TopKlienciCard onNavigate={onNavigate} />

      {/* Tabela szczegółowa — z auto-collapse pustych miesięcy */}
      <div className="ds-card overflow-hidden">
        <div className="flex items-center justify-between" style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
          <h2 style={{ fontSize: '12px', fontWeight: 700, color: 'var(--ink)' }}>{t('finances.detailedReport')}</h2>
          {puste.length > 0 && (
            <button onClick={() => setShowZeroMonths(s => !s)}
              className="hover:underline"
              style={{ fontSize: '10px', color: 'var(--ink3)', background: 'none', border: 'none', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
              {showZeroMonths
                ? t('dashboardV2.hideEmptyMonthsAction', { label: t('dashboardV2.emptyMonths', { n: puste.length, count: puste.length }) })
                : t('dashboardV2.showEmptyMonthsAction', { label: t('dashboardV2.emptyMonths', { n: puste.length, count: puste.length }) })}
            </button>
          )}
        </div>
        <div className="overflow-auto" style={{ maxHeight: '70vh' }}>
          <table className="w-full" style={{ borderCollapse: 'separate', borderSpacing: 0, minWidth: '640px' }}>
            <thead>
              <tr>
                <Th sticky left>{t('finances.month')}</Th>
                <Th right>{t('finances.revenueHosting')}</Th>
                <Th right>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    {t('finances.packages_col')}
                    <HelpHint text={t('help.packages')} label={t('finances.packages_col')} />
                  </span>
                </Th>
                <Th right>{t('finances.total')}</Th>
                <Th right>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    {t('finances.mpCost')}
                    <HelpHint text={t('help.matterportCost')} label={t('finances.mpCost')} />
                  </span>
                </Th>
                <Th right>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    {t('finances.netProfit')}
                    <HelpHint text={t('help.netProfit')} label={t('finances.netProfit')} />
                  </span>
                </Th>
              </tr>
            </thead>
            <tbody>
              {visibleMies.map(w => {
                const key = `${w.rok}-${w.miesiac}`;
                const isEditing = editKoszt[key] !== undefined;
                const currentKoszt = koszty.find(k => k.rok === w.rok && k.miesiac === w.miesiac);
                const lacznie = w.hosting + w.pakiety;
                const isEmpty = lacznie === 0 && w.koszt === 0;
                return (
                  <tr key={key} className="transition-colors"
                    style={{ opacity: isEmpty ? 0.5 : 1 }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <Td sticky left bold>
                      {MONTHS[w.miesiac - 1]} {w.rok}
                    </Td>
                    <Td right mono color="var(--ok2)" muted={w.hosting === 0}>
                      {formatCurrency(w.hosting)}
                    </Td>
                    <Td right mono color="var(--acc2)" muted={w.pakiety === 0}>
                      {formatCurrency(w.pakiety)}
                    </Td>
                    <Td right mono color="var(--ink)" bold muted={lacznie === 0}>
                      {formatCurrency(lacznie)}
                    </Td>
                    <Td right>
                      <div className="flex items-center justify-end gap-2">
                        {isEditing ? (
                          <>
                            <input
                              type="number"
                              autoFocus
                              style={{ background: 'var(--bg3)', border: '1px solid var(--acc)', borderRadius: '6px', color: 'var(--ink)', fontSize: '11px', padding: '3px 8px', width: '96px', textAlign: 'right' }}
                              className="focus:outline-none"
                              value={editKoszt[key]}
                              onChange={e => handleKosztChange(w.rok, w.miesiac, e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') handleSaveKoszt(w.rok, w.miesiac);
                                if (e.key === 'Escape') setEditKoszt(prev => { const n = { ...prev }; delete n[key]; return n; });
                              }}
                            />
                            <button
                              onClick={() => handleSaveKoszt(w.rok, w.miesiac)}
                              disabled={savingKoszt}
                              aria-label={t('dashboardV2.saveAria')}
                              style={{ color: 'var(--ok2)', display: 'inline-flex', alignItems: 'center', padding: '2px', background: 'none', border: 'none', cursor: 'pointer' }}
                            ><IconCheck className="w-3.5 h-3.5" /></button>
                            <button
                              onClick={() => setEditKoszt(prev => { const n = { ...prev }; delete n[key]; return n; })}
                              aria-label={t('dashboardV2.cancelAria')}
                              style={{ color: 'var(--ink2)', display: 'inline-flex', alignItems: 'center', padding: '2px', background: 'none', border: 'none', cursor: 'pointer' }}
                            ><IconX className="w-3.5 h-3.5" /></button>
                          </>
                        ) : (
                          <button
                            onClick={() => handleKosztChange(w.rok, w.miesiac, currentKoszt?.kwota || 0)}
                            className="hover:underline"
                            style={{
                              fontFamily: 'monospace', fontWeight: 700,
                              color: w.koszt > 0 ? 'var(--err2)' : 'var(--ink3)',
                              background: 'none', border: 'none', cursor: 'pointer',
                              padding: '2px 6px', borderRadius: '4px',
                              display: 'inline-flex', alignItems: 'center', gap: '6px',
                            }}
                            title={t('dashboardV2.clickToEdit')}>
                            {formatCurrency(w.koszt)}
                            <IconEdit className="w-3 h-3" style={{ opacity: 0.4 }} />
                          </button>
                        )}
                      </div>
                    </Td>
                    <Td right mono bold color={w._zysk >= 0 ? (w._zysk > 0 ? 'var(--ok2)' : 'var(--ink3)') : 'var(--err2)'}>
                      {formatCurrency(w._zysk)}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <Td sticky left bold totalRow>{t('finances.totalRow')}</Td>
                <Td right mono color="var(--ok2)" totalRow>{formatCurrency(roczne.hosting)}</Td>
                <Td right mono color="var(--acc2)" totalRow>{formatCurrency(roczne.pakiety)}</Td>
                <Td right mono color="var(--ink)" totalRow>{formatCurrency(roczne.przychod)}</Td>
                <Td right mono color="var(--err2)" totalRow>{formatCurrency(roczne.koszt)}</Td>
                <Td right mono color={roczne.zysk >= 0 ? 'var(--ok2)' : 'var(--err2)'} totalRow>
                  {formatCurrency(roczne.zysk)}
                </Td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Pomocnicze cells ─────────────────────────────────────────────────────────

function Th({ children, right, sticky, left }) {
  return (
    <th style={{
      fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)',
      padding: '10px 16px', textAlign: right ? 'right' : 'left',
      ...(sticky ? { position: 'sticky', top: 0, zIndex: 2, background: 'var(--bg-card)', borderBottom: '1px solid var(--border)' } : {}),
      ...(left ? { left: 0, zIndex: 3, borderRight: '1px solid var(--border)' } : {}),
    }}>
      {children}
    </th>
  );
}

function Td({ children, right, sticky, left, bold, mono, color, muted, totalRow }) {
  return (
    <td style={{
      padding: '11px 16px',
      textAlign: right ? 'right' : 'left',
      fontSize: '11px',
      fontFamily: mono ? 'monospace' : undefined,
      fontWeight: bold ? 700 : 500,
      color: muted ? 'var(--ink3)' : (color || 'var(--ink)'),
      borderTop: totalRow ? '2px solid var(--border)' : undefined,
      borderBottom: totalRow ? undefined : '1px solid var(--border)',
      ...(sticky ? { position: 'sticky', left: 0, zIndex: 1, background: totalRow ? 'var(--bg2)' : 'var(--bg-card)', borderRight: '1px solid var(--border)' } : {}),
      ...(totalRow && !sticky ? { background: 'var(--bg2)' } : {}),
    }}>
      {children}
    </td>
  );
}
